import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/SimpleAuthContext';
import { useToast } from '../../contexts/ToastContext';
import { opportunityService } from '../../services/opportunityService';
import './OpportunityApplication.css';

const OpportunityApplication = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  
  const [opportunity, setOpportunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [applicationData, setApplicationData] = useState({
    motivation_letter: '',
    additional_notes: ''
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadOpportunity();
  }, [id, user, navigate]);

  const loadOpportunity = async () => {
    try {
      setLoading(true);
      const data = await opportunityService.getOpportunity(id);
      setOpportunity(data);
      
      // Check if user can apply
      if (data.user_has_applied) {
        showError('You have already applied for this opportunity');
        navigate(`/opportunities/${id}`);
        return;
      }
      
      if (!data.can_apply) {
        showError('This opportunity is no longer accepting applications');
        navigate(`/opportunities/${id}`);
        return;
      }
    } catch (error) {
      console.error('Error loading opportunity:', error);
      showError('Failed to load opportunity details');
      navigate('/opportunities');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setApplicationData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!applicationData.motivation_letter.trim()) {
      showError('Please provide your motivation for applying');
      return;
    }

    try {
      setSubmitting(true);
      
      const submitData = {
        opportunity: parseInt(id),
        motivation_letter: applicationData.motivation_letter,
        additional_notes: applicationData.additional_notes
      };

      await opportunityService.applyForOpportunity(submitData);
      
      showSuccess(`Successfully applied for "${opportunity.title}"!`, 5000);
      navigate('/opportunities', { 
        state: { 
          message: 'Application submitted successfully!',
          type: 'success'
        }
      });
    } catch (error) {
      console.error('Error submitting application:', error);
      
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === 'object') {
          const errorMessages = Object.values(errorData).flat();
          showError(`Application failed: ${errorMessages[0]}`, 6000);
        } else {
          showError(`Application failed: ${errorData}`, 6000);
        }
      } else {
        showError('Failed to submit application. Please try again.', 6000);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="opportunity-application">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading opportunity details...</p>
        </div>
      </div>
    );
  }

  if (!opportunity) {
    return (
      <div className="opportunity-application">
        <div className="error">
          <h2>Opportunity Not Found</h2>
          <p>The opportunity you're trying to apply for could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="opportunity-application">
      <div className="application-header">
        <h1>📝 Apply for Opportunity</h1>
        <div className="opportunity-info">
          <h2>{opportunity.title}</h2>
          <div className="opportunity-meta">
            <span className="type-badge">{opportunity.opportunity_type}</span>
            {opportunity.location && <span className="location">📍 {opportunity.location}</span>}
            <span className="deadline">
              ⏰ Apply by {new Date(opportunity.application_deadline).toLocaleDateString()}
            </span>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="application-form">
        <div className="form-section">
          <h3>Application Details</h3>
          
          <div className="form-group">
            <label htmlFor="motivation_letter">Why are you interested in this opportunity? *</label>
            <textarea
              id="motivation_letter"
              name="motivation_letter"
              value={applicationData.motivation_letter}
              onChange={handleInputChange}
              placeholder="Explain your motivation, why you're a good fit, your relevant experience, and availability..."
              rows={6}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="additional_notes">Additional Information</label>
            <textarea
              id="additional_notes"
              name="additional_notes"
              value={applicationData.additional_notes}
              onChange={handleInputChange}
              placeholder="Any additional information you'd like to share..."
              rows={3}
            />
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate(`/opportunities/${id}`)}
            className="btn btn-outline"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : 'Submit Application'}
          </button>
        </div>
      </form>

      <div className="application-info">
        <h3>What happens next?</h3>
        <ul>
          <li>Your application will be reviewed by our team</li>
          <li>You'll receive updates on your application status</li>
          <li>If selected, you'll be contacted for next steps</li>
          <li>You can track your application status in your dashboard</li>
        </ul>
      </div>
    </div>
  );
};

export default OpportunityApplication;
